<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { useRouter } from "vue-router";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import type { Charter } from "@/api/client/models/index.ts";
import {useCharterCrewBriefingStore} from "@/charter/stores/charter-crew-briefing-store.ts";
import CharterCrewBriefingActions from "@/charter/components/CharterCrewBriefingActions.vue";
import CrewBriefingDownloadDialog from "@/charter/components/CrewBriefingDownloadDialog.vue";

const charterCrewBriefingStore = useCharterCrewBriefingStore();
const router = useRouter();

const myTheme = themeQuartz.withParams({});

const selectedDate = ref<Date | undefined>(new Date());
const selectedDownloadCharterId = ref<number | null>(null);
const downloadDialogVisible = ref(false);

const gridOptions: GridOptions<Charter> = {
  pagination: true,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
  },
  columnDefs: [
    { field: "departureDate", headerName: "Date", filter: true },
    { field: "aircraftType", headerName: "A/C", filter: true },
    { field: "client", filter: true },
    { field: "flightNumber", headerName: "Flt Nr", filter: true },
    { field: "stretch", filter: true },
    { field: "std", filter: true },
    { field: "sta", filter: true },
    {
      headerName: "",
      filter: false,
      cellRenderer: CharterCrewBriefingActions,
      cellRendererParams: {
        onUpdate: (id: number) => openCrewBriefing(id),
        onDownload: (id: number) => openDownloadDialog(id),
      },
      sortable: false,
      suppressHeaderMenuButton: true,
      minWidth: 150,
      pinned: "right",
      autoHeight: true,
    },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);

onMounted(async () => {
  await charterCrewBriefingStore.fetchCrewBriefing(selectedDate.value);
});

function openCrewBriefing(id: number): void {
  void router.push({
    name: "charter-flight-crew-briefing",
    params: { charterId: id },
  });
}

function openDownloadDialog(id: number): void {
  selectedDownloadCharterId.value = id;
  downloadDialogVisible.value = true;
}

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

function refreshData() {
  charterCrewBriefingStore.fetchCrewBriefing(selectedDate.value);
}

function onDateChange() {
  charterCrewBriefingStore.fetchCrewBriefing(selectedDate.value);
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h1>Charter Crew Briefing</h1>
      <div class="flex items-center gap-2">
        <p-calendar
          v-model="selectedDate"
          dateFormat="dd/mm/yy"
          showIcon
          showButtonBar
          @date-select="onDateChange"
          @clear-click="onDateChange"
          placeholder="Select date"
        />
        <p-button link @click="refreshData" :loading="charterCrewBriefingStore.isLoading">
          <span class="material-icons" title="refresh">refresh</span>
        </p-button>
      </div>
    </div>

    <div v-if="charterCrewBriefingStore.hasError" class="p-message p-message-error">
      <div class="p-message-wrapper">
        <span class="p-message-icon pi pi-times-circle"></span>
        <div class="p-message-text">
          {{ charterCrewBriefingStore.error }}
        </div>
        <button class="p-message-close p-link" @click="charterCrewBriefingStore.clearError">
          <span class="pi pi-times"></span>
        </button>
      </div>
    </div>

    <AgGridVue
      :rowData="charterCrewBriefingStore.crewBriefing"
      :loading="charterCrewBriefingStore.isLoading"
      :gridOptions="gridOptions"
      ref="gridRef"
      :theme="myTheme"
      style="width: 100%; height: 100%"
      @grid-ready="onGridReady"
    />

    <CrewBriefingDownloadDialog
      v-if="selectedDownloadCharterId !== null"
      v-model:visible="downloadDialogVisible"
      :charterId="selectedDownloadCharterId"
    />
  </div>
</template>

<style scoped lang="scss">
h1 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

.p-message {
  margin: 0;
}
</style>
